import { readFileSync, existsSync, readdirSync } from "node:fs";
import { resolve } from "node:path";
import type { Claim, DriftIssue, ProjectModel } from "../../types.js";
import { isSoftClaim } from "../confidence.js";

/** Check that claimed npm/yarn/make commands actually exist */
export function checkCommands(
  claims: Claim[],
  projectRoot: string,
  project?: ProjectModel,
  subProjectRoot?: string
): DriftIssue[] {
  const issues: DriftIssue[] = [];
  const commandClaims = claims.filter(
    (c) => c.kind === "command" && !c.negated
  );

  // Resolve script/target/service files relative to the sub-project root when
  // it differs from projectRoot (monorepo with stub parent package.json).
  const scriptsRoot = subProjectRoot ?? projectRoot;

  const pkgScripts = loadPackageScripts(scriptsRoot);
  const makeTargets = loadMakeTargets(scriptsRoot);
  const shellScripts = loadShellScripts(scriptsRoot);
  const dcServices = loadDockerComposeServices(scriptsRoot);
  const workspaceLookup = buildWorkspaceScriptLookup(project);

  for (const claim of commandClaims) {
    const cmd = claim.value.trim();

    const scriptRef = parseScriptReference(cmd);
    if (scriptRef) {
      if (!scriptExists(scriptRef.script, scriptRef.workspaceSelector, pkgScripts, workspaceLookup)) {
        issues.push({
          code: "DEAD_COMMAND",
          severity: isSoftClaim(claim) ? "warning" : "error",
          file: claim.source,
          line: claim.line,
          message: scriptRef.workspaceSelector
            ? `Script "${scriptRef.script}" not found for workspace "${scriptRef.workspaceSelector}"`
            : `Script "${scriptRef.script}" not found in package.json scripts`,
          explanation: scriptRef.workspaceSelector
            ? `Command was parsed as a workspace-scoped script reference and matched against workspace "${scriptRef.workspaceSelector}".`
            : "Command was parsed as a root package script reference and matched against package.json scripts.",
          suggestion: scriptRef.workspaceSelector
            ? `Document a real script for workspace "${scriptRef.workspaceSelector}", or change this command to one of that workspace's existing scripts.`
            : `Update the scaffold to an existing package.json script, or add "${scriptRef.script}" to the root scripts if it should exist.`,
          claim,
        });
      }
      continue;
    }

    // make <target>
    const makeMatch = cmd.match(/^make\s+(\S+)/);
    if (makeMatch) {
      const target = makeMatch[1];
      if (makeTargets && !makeTargets.has(target)) {
        issues.push({
          code: "DEAD_COMMAND",
          severity: isSoftClaim(claim) ? "warning" : "error",
          file: claim.source,
          line: claim.line,
          message: `Make target "${target}" not found in Makefile`,
          explanation: "Command was parsed as a Make target reference and matched against the local Makefile.",
          suggestion: `Update the scaffold to an existing Make target, or add "${target}" to the Makefile if it should exist.`,
          claim,
        });
      }
      continue;
    }

    // ./scripts/<name> or bash scripts/<name>
    const scriptMatch = cmd.match(/^(?:bash\s+|sh\s+|\.\/)?scripts\/([^\s]+)/);
    if (scriptMatch) {
      const scriptName = scriptMatch[1];
      if (shellScripts && !shellScripts.has(scriptName)) {
        issues.push({
          code: "DEAD_COMMAND",
          severity: isSoftClaim(claim) ? "warning" : "error",
          file: claim.source,
          line: claim.line,
          message: `Script "scripts/${scriptName}" not found in scripts/ directory`,
          suggestion: `Update the scaffold or create scripts/${scriptName}.`,
          claim,
        });
      }
      continue;
    }

    // docker compose <service>
    const dockerMatch = cmd.match(/^docker[\s-]compose\s+(?:up|run|exec)\s+(\S+)/);
    if (dockerMatch) {
      const service = dockerMatch[1];
      if (dcServices && !dcServices.has(service)) {
        issues.push({
          code: "DEAD_COMMAND",
          severity: "warning",
          file: claim.source,
          line: claim.line,
          message: `Docker Compose service "${service}" not found in compose file`,
          suggestion: `Update the scaffold or add "${service}" to your Docker Compose config.`,
          claim,
        });
      }
    }
  }

  return issues;
}

interface ScriptReference {
  script: string;
  workspaceSelector: string | null;
}

function loadPackageScripts(
  projectRoot: string
): Set<string> | null {
  const pkgPath = resolve(projectRoot, "package.json");
  if (!existsSync(pkgPath)) return null;
  try {
    const pkg = JSON.parse(readFileSync(pkgPath, "utf-8"));
    return new Set(Object.keys(pkg.scripts ?? {}));
  } catch {
    return null;
  }
}

function buildWorkspaceScriptLookup(project?: ProjectModel): Map<string, Set<string>> {
  const lookup = new Map<string, Set<string>>();
  if (!project) return lookup;

  for (const workspace of project.workspaces) {
    const scripts = new Set(Object.keys(workspace.manifest.scripts));
    const aliases = new Set<string>([workspace.path]);
    if (workspace.manifest.name) aliases.add(workspace.manifest.name);
    const basename = workspace.path.split("/").pop();
    if (basename) aliases.add(basename);

    for (const alias of aliases) {
      lookup.set(alias, scripts);
    }
  }

  return lookup;
}

function parseScriptReference(command: string): ScriptReference | null {
  const npmMatch = command.match(/^npm\s+run\s+([^\s]+)/);
  if (npmMatch) {
    return {
      script: npmMatch[1],
      workspaceSelector: extractWorkspaceSelector(command),
    };
  }

  const bunMatch = command.match(/^bun\s+run\s+([^\s]+)/);
  if (bunMatch) {
    return {
      script: bunMatch[1],
      workspaceSelector: extractWorkspaceSelector(command),
    };
  }

  const yarnWorkspaceMatch = command.match(/^yarn\s+workspace\s+([^\s]+)\s+([^\s]+)/);
  if (yarnWorkspaceMatch) {
    return {
      workspaceSelector: yarnWorkspaceMatch[1],
      script: yarnWorkspaceMatch[2],
    };
  }

  const yarnMatch = command.match(/^yarn\s+([^\s]+)/);
  if (yarnMatch) {
    return {
      script: yarnMatch[1],
      workspaceSelector: extractWorkspaceSelector(command),
    };
  }

  if (command.startsWith("pnpm ")) {
    const remainder = command.slice("pnpm ".length).trim();
    const script = extractTrailingScript(remainder);
    if (!script) return null;
    return {
      script,
      workspaceSelector: extractWorkspaceSelector(command),
    };
  }

  const turboMatch = command.match(/^turbo\s+run\s+([^\s]+)(?:\s+(.+))?$/);
  if (turboMatch) {
    return {
      script: turboMatch[1],
      workspaceSelector: extractWorkspaceSelector(command),
    };
  }

  return null;
}

function extractWorkspaceSelector(command: string): string | null {
  const filterMatch = command.match(/--filter(?:=|\s+)([^\s]+)/);
  if (filterMatch) return filterMatch[1];

  const workspaceFlagMatch = command.match(/--workspace(?:=|\s+)([^\s]+)/);
  if (workspaceFlagMatch) return workspaceFlagMatch[1];

  return null;
}

function extractTrailingScript(rest: string): string | null {
  const tokens = rest.split(/\s+/).filter(Boolean);
  for (let index = tokens.length - 1; index >= 0; index--) {
    if (!tokens[index].startsWith("-")) {
      return tokens[index];
    }
  }
  return null;
}

function scriptExists(
  script: string,
  workspaceSelector: string | null,
  rootScripts: Set<string> | null,
  workspaceLookup: Map<string, Set<string>>
): boolean {
  if (workspaceSelector) {
    if (workspaceLookup.size === 0) return true;
    const workspaceScripts = workspaceLookup.get(workspaceSelector);
    return workspaceScripts ? workspaceScripts.has(script) : false;
  }

  if (!rootScripts) return true;
  return rootScripts.has(script);
}

function loadShellScripts(projectRoot: string): Set<string> | null {
  const scriptsDir = resolve(projectRoot, "scripts");
  if (!existsSync(scriptsDir)) return null;
  try {
    const entries = readdirSync(scriptsDir);
    return new Set(entries);
  } catch {
    return null;
  }
}

function loadDockerComposeServices(projectRoot: string): Set<string> | null {
  const candidates = ["docker-compose.yml", "docker-compose.yaml", "compose.yml", "compose.yaml"];
  for (const name of candidates) {
    const path = resolve(projectRoot, name);
    if (!existsSync(path)) continue;
    try {
      const content = readFileSync(path, "utf-8");
      const services = new Set<string>();
      // Simple YAML parsing: lines matching "  <service>:" under "services:" section
      let inServices = false;
      for (const line of content.split("\n")) {
        if (/^services:\s*$/.test(line)) { inServices = true; continue; }
        if (inServices && /^\S/.test(line)) break; // next top-level key
        if (inServices) {
          const match = line.match(/^\s{2}(\w[\w-]*):/);
          if (match) services.add(match[1]);
        }
      }
      return services.size > 0 ? services : null;
    } catch {
      continue;
    }
  }
  return null;
}

function loadMakeTargets(projectRoot: string): Set<string> | null {
  const makePath = resolve(projectRoot, "Makefile");
  if (!existsSync(makePath)) return null;
  try {
    const content = readFileSync(makePath, "utf-8");
    const targets = new Set<string>();
    for (const line of content.split("\n")) {
      const match = line.match(/^(\w[\w-]*):/);
      if (match) targets.add(match[1]);
    }
    return targets;
  } catch {
    return null;
  }
}
